import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import './ModifierMembre.css';

interface Membre {
  photo: string;
  loge: string; // loge "courante" (sélectionnée)
  loges?: string[];
  fonctionsParLoge?: Record<string, string>;

  nom: string;
  prenom: string;
  adresse: string;
  codePostal: string;
  ville: string;
  mail: string;
  telephone: string;

  statut: string;
  grades: string;

  // valeur affichée dans le select "fonction"
  fonction: string;

  dateNaissance: string;
  administrateur: string;
}

interface Loge {
  id: string;
  nom: string;
}

interface Grade {
  id: string;
}

interface Fonction {
  id: string;
}

function ModifierMembre() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [membre, setMembre] = useState<Membre | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [loges, setLoges] = useState<Loge[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [fonctions, setFonctions] = useState<Fonction[]>([]);

  // Utilisateur connecté
  const [isAdmin, setIsAdmin] = useState(false);
  const [myFonctionsParLoge, setMyFonctionsParLoge] = useState<Record<string, string>>({});
  const [canEditUser, setCanEditUser] = useState(false);

  // ====== Charger utilisateur connecté (admin + fonctionsParLoge) ======
  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user?.email) return;

        const q = query(collection(db, 'Membres'), where('mail', '==', user.email));
        const snap = await getDocs(q);

        if (!snap.empty) {
          const me = snap.docs[0].data() as any;
          const admin = me.administrateur === 'oui';
          setIsAdmin(admin);

          const fp = (me.fonctionsParLoge || {}) as Record<string, string>;
          setMyFonctionsParLoge(fp);

          // base : admin => peut, sinon recalcul après chargement du membre
          setCanEditUser(admin);
        }
      } catch (e) {
        console.error(e);
      }
    });

    return () => unsub();
  }, []);

  // ====== Charger listes ======
  useEffect(() => {
    const fetchData = async () => {
      try {
        const db = getFirestore();

        const logesSnapshot = await getDocs(collection(db, 'Loges'));
        setLoges(
          logesSnapshot.docs.map((d) => ({
            id: d.id,
            nom: (d.data() as any).nom || d.id,
          }))
        );

        const gradesSnapshot = await getDocs(collection(db, 'Grades'));
        setGrades(gradesSnapshot.docs.map((d) => ({ id: d.id })));

        const fonctionsSnapshot = await getDocs(collection(db, 'Fonctions'));
        setFonctions(fonctionsSnapshot.docs.map((d) => ({ id: d.id })));
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
      }
    };

    fetchData();
  }, []);

  // ====== Charger membre à modifier (avec support ?loge=...) ======
  useEffect(() => {
    const fetchMembre = async () => {
      if (!id) {
        setError('Identifiant du membre manquant');
        setLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const membreRef = doc(db, 'Membres', id);
        const membreDoc = await getDoc(membreRef);

        if (!membreDoc.exists()) {
          setError('Membre non trouvé');
          setLoading(false);
          return;
        }

        const data = membreDoc.data() as any;

        // ✅ loge demandée depuis MaLoge
        const params = new URLSearchParams(location.search);
        const requestedLogeRaw = params.get('loge');
        const requestedLoge = requestedLogeRaw ? decodeURIComponent(requestedLogeRaw) : '';

        // Map des fonctions
        const fp: Record<string, string> = data.fonctionsParLoge || {};

        // fallback loge si pas de paramètre
        const fallbackLoge = data.loge || (Array.isArray(data.loges) && data.loges[0]) || '';
        const currentLoge = requestedLoge || fallbackLoge;

        // Fonction pour la loge sélectionnée
        const currentFonction = fp[currentLoge] || data.fonction || '';

        const membreLoaded: Membre = {
          photo: data.photo || '',
          loge: currentLoge,
          loges: data.loges,
          fonctionsParLoge: fp,

          nom: data.nom || '',
          prenom: data.prenom || '',
          adresse: data.adresse || '',
          codePostal: data.codePostal || '',
          ville: data.ville || '',
          mail: data.mail || '',
          telephone: data.telephone || '',

          statut: data.statut || 'Valide',
          grades: data.grades || '',

          fonction: currentFonction,
          dateNaissance: data.dateNaissance || '',
          administrateur: data.administrateur || 'non',
        };

        setMembre(membreLoaded);
      } catch (err) {
        console.error('Erreur lors de la récupération:', err);
        setError('Erreur lors de la récupération du membre');
      } finally {
        setLoading(false);
      }
    };

    fetchMembre();
  }, [id, location.search]);

  // ====== Recalcul droit: admin OR (role sur la loge sélectionnée) ======
  useEffect(() => {
    if (!membre) return;

    if (isAdmin) {
      setCanEditUser(true);
      return;
    }

    const role = myFonctionsParLoge?.[membre.loge];
    setCanEditUser(role === 'Vénérable' || role === 'Secrétaire');
  }, [membre, isAdmin, myFonctionsParLoge]);

  // ====== Change handler avec synchronisation fonctionsParLoge ======
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setMembre((prev) => {
      if (!prev) return null;

      // Changement de loge -> mettre fonction affichée = fonctionsParLoge[loge]
      if (name === 'loge') {
        const newLoge = value;
        const f = prev.fonctionsParLoge?.[newLoge] || '';
        return { ...prev, loge: newLoge, fonction: f };
      }

      // Changement de fonction -> mettre à jour la map
      if (name === 'fonction') {
        const currentLoge = prev.loge;
        const nextMap = { ...(prev.fonctionsParLoge || {}) };
        if (currentLoge) nextMap[currentLoge] = value;
        return { ...prev, fonction: value, fonctionsParLoge: nextMap };
      }

      return { ...prev, [name]: value } as Membre;
    });
  };

  // ====== Enregistrement ======
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!id || !membre) return;

    if (!canEditUser) {
      setError("Vous n'avez pas les droits pour modifier ce membre sur cette loge.");
      return;
    }

    try {
      setError('');
      const db = getFirestore();

      const logeCourante = membre.loge;
      if (!logeCourante) {
        setError("Veuillez sélectionner une loge avant d'enregistrer.");
        return;
      }

      const nouvelleFonction =
        membre.fonctionsParLoge?.[logeCourante] ?? membre.fonction ?? '';

      // Sécurité administrateur
      const adminValue = isAdmin ? (membre.administrateur || 'non') : 'non';

      const updateData: any = {
        nom: membre.nom,
        prenom: membre.prenom,
        adresse: membre.adresse,
        codePostal: membre.codePostal,
        ville: membre.ville,
        mail: membre.mail,
        telephone: membre.telephone,
        dateNaissance: membre.dateNaissance,
        grades: membre.grades,

        statut: membre.statut,

        administrateur: adminValue,

        dateModification: serverTimestamp(),
      };

      // ✅ Mise à jour du rôle UNIQUEMENT pour la loge courante
      updateData[`fonctionsParLoge.${logeCourante}`] = nouvelleFonction;

      await updateDoc(doc(db, 'Membres', id), updateData);

      navigate(isAdmin ? '/mapage/membres' : '/mapage/ma-loge');;
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la mise à jour du membre');
    }
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!membre) return <div>Membre non trouvé</div>;

  return (
    <div className="mes-informations-container">
      <h1>Modifier les Informations</h1>

      {!canEditUser && (
        <div className="error-message">
          Vous n’avez pas les droits de modification pour la loge sélectionnée.
        </div>
      )}

      <div className="info-card">
        <div className="photo-section">
          {membre.photo ? (
            <img src={membre.photo} alt="Profil" className="profile-photo" />
          ) : (
            <div className="no-photo">Aucune photo</div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="info-grid">
          <div className="info-group">
            <label>Nom:</label>
            <input
              type="text"
              name="nom"
              value={membre.nom}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Prénom:</label>
            <input
              type="text"
              name="prenom"
              value={membre.prenom}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Date de Naissance:</label>
            <input
              type="text"
              name="dateNaissance"
              value={membre.dateNaissance}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Loge (loge courante):</label>
            <select
              name="loge"
              value={membre.loge}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            >
              <option value="">Sélectionner une loge</option>
              {loges.map((loge) => (
                <option key={loge.id} value={loge.id}>
                  {loge.nom}
                </option>
              ))}
            </select>
          </div>

          <div className="info-group">
            <label>Grade:</label>
            <select
              name="grades"
              value={membre.grades}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            >
              <option value="">Sélectionner un grade</option>
              {grades.map((grade) => (
                <option key={grade.id} value={grade.id}>
                  {grade.id}
                </option>
              ))}
            </select>
          </div>

          <div className="info-group">
            <label>Fonction (pour CETTE loge):</label>
            <select
              name="fonction"
              value={membre.fonction}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            >
              <option value="">Sélectionner une fonction</option>
              {fonctions.map((fonction) => (
                <option key={fonction.id} value={fonction.id}>
                  {fonction.id}
                </option>
              ))}
            </select>
          </div>

          <div className="info-group">
            <label>Statut:</label>
            <select
              name="statut"
              value={membre.statut}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            >
              <option value="Valide">Valide</option>
              <option value="Non Valide">Non Valide</option>
            </select>
          </div>

          {/* ✅ Administrateur visible uniquement si admin */}
          {isAdmin && (
            <div className="info-group">
              <label>Administrateur:</label>
              <select
                name="administrateur"
                value={membre.administrateur}
                onChange={handleChange}
                className="info-input"
                disabled={!canEditUser}
              >
                <option value="oui">oui</option>
                <option value="non">non</option>
              </select>
            </div>
          )}

          <div className="info-group full-width">
            <label>Adresse:</label>
            <input
              type="text"
              name="adresse"
              value={membre.adresse}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Code Postal:</label>
            <input
              type="text"
              name="codePostal"
              value={membre.codePostal}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Ville:</label>
            <input
              type="text"
              name="ville"
              value={membre.ville}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Email:</label>
            <input
              type="email"
              name="mail"
              value={membre.mail}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="info-group">
            <label>Téléphone:</label>
            <input
              type="text"
              name="telephone"
              value={membre.telephone}
              onChange={handleChange}
              className="info-input"
              disabled={!canEditUser}
            />
          </div>

          <div className="buttons-container">
            <button type="submit" className="save-btn" disabled={!canEditUser}>
              Enregistrer
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate(isAdmin ? '/mapage/membres' : '/mapage/ma-loge')}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModifierMembre;
